/*
 * vms-elevation.js
 * ---------------------------------------------------------------------------
 * The geometry of a ceiling-mount elevation: where the structure, the drop
 * ceiling, the plate, the mast, the head and the TV sit relative to each other,
 * and what the drop is allowed to be.
 *
 * PURE GEOMETRY ONLY. No DOM, no drawing, no drag. That is deliberate: the
 * numbers below are the part that has to be right, so they are kept separately
 * testable and cannot be broken by a UI change. The interactive scene is layered
 * on top of this and reads it, never the other way round.
 *
 * ONE REFERENCE, MEASURED DOWNWARDS
 * Every depth here is inches measured DOWN from the building structure, which is
 * depth 0. That matches what the configurator already tells the customer: with a
 * drop ceiling the drop is measured from the real structure above the tiles, not
 * from the tile line, because the tile grid is not structure and carries nothing.
 *
 *   depth 0            building structure (joist, deck, beam, strut)
 *   plateOffset        bottom of the ceiling plate, where the pipe starts
 *   tileDepth          underside of the drop ceiling, when there is one
 *   drop - C/2         top of the TV CABINET (the physical box, bezel included, and
 *                      since 2026-07-28 the top of the DRAWN rectangle as well)
 *   drop - H/2         top of the TV SCREEN (the picture)
 *   drop               the mount hole pattern, and the middle of both
 *   drop - headOffset  bottom of the pipe stack (ABOVE `drop` for a normal head,
 *                      BELOW it for a negative-offset head, which reaches back up)
 *   drop + H/2         bottom of the TV screen
 *   drop + C/2         bottom of the TV cabinet
 *
 * H is the screen height and C is the cabinet height, C = H + 1 in. See the next
 * section: they are two different numbers with two different jobs and both are kept.
 *
 * `drop` is the customer's target and is measured structure to mid-screen, which
 * is the same reference dropRefLabel() already states in the configurator. The
 * mast length that has to be solved is therefore
 *   drop - plateOffset - headOffset
 * which is exactly the mastTarget the solver already computes. This file does not
 * duplicate that arithmetic, it just agrees with it.
 *
 * READ THAT EQUATION BACKWARDS AND IT IS THE FLOOR. A mast has a shortest
 * possible length, so the shortest finished drop is
 *   plateOffset + headOffset + minMastLength
 * and nothing shallower is a thing we can ship. That is one of the two structural
 * constraints minDrop() gained on 2026-07-27; the other is that the TV cannot rise
 * into the CEILING it hangs from. Both apply whether or not there is a drop
 * ceiling, and their absence was a real, owner-reported defect: see minDrop's
 * header.
 * `minMastLength` is an INPUT, never a constant in here, because the pipe pool it
 * comes from lives in the data layer and changes when the catalog does.
 *
 * SCREEN HEIGHT FROM THE DIAGONAL
 * A TV size is a DIAGONAL. For a 16:9 screen, height = diagonal * 9/sqrt(16^2+9^2)
 * = 0.490261. So a 70 in TV screen is 34.32 in tall. Owner supplied 0.4903, which
 * matches the exact factor to four decimal places; the exact value is used here.
 *
 * TWO HEIGHTS, TWO JOBS, AND YOU NEED BOTH (owner ruling 2026-07-27, researched,
 * FINAL, and explicitly not to be re-litigated or "improved" in a later session)
 * The 16:9 figure above is the SCREEN, the active picture area. A real TV is taller
 * than its screen, because the bezel sits OUTSIDE the diagonal measurement, and the
 * bezel is what collides with things. So:
 *
 *   tvScreenHeight(d)   = d * 0.490261        the picture. The pure 16:9 helper.
 *   tvCabinetHeight(d)  = that + 1 INCH       the physical box. What must CLEAR.
 *
 * The 1 in allowance is the owner's researched final figure. It is SPLIT EVENLY,
 * half an inch above the screen and half below (owner ruling, asked rather than
 * assumed, 2026-07-28). That split is load-bearing in a way that is easy to miss:
 * an even split leaves mid-cabinet exactly equal to mid-screen, so `drop` still
 * means the same thing it always did and the centred-hole-pattern assumption in
 * tvTopDepth is UNTOUCHED by this change. Do not read the bezel allowance as having
 * fixed that assumption; it is a separate, still-open weakness (see tvTopDepth).
 *
 * THE DRAWING USES THE CABINET TOO (owner correction 2026-07-28, and it SUPERSEDES
 * the "everything DRAWN stays on the SCREEN" half of the rule this file shipped
 * with earlier the same day)
 * The owner corrected his own earlier guidance in his own words: "My intention was
 * to alter the equation for calculating the display of the 'screen': diagonal screen
 * size x .4903 + 1, and then draw a 16:9 aspect ratio rectangle 'screen' from that
 * number. The whole add 1/2 at top and 1/2 at bottom is overkill."
 *
 * So the drawn TV is ONE 16:9 rectangle computed from the INFLATED height:
 *   drawn height = tvCabinetHeight(d)
 *   drawn width  = tvCabinetWidth(d) = that height * 16/9
 * and the drawing and the clearance numbers now use the SAME height, so they agree
 * exactly at every hard stop. That closed a standing open item: at a stop the readout
 * printed the top of the TV exactly AT the limit while the drawn screen sat half an
 * inch clear of it, with nothing on screen to explain the difference.
 *
 * [WARN] WHAT DID NOT MOVE, AND MUST NOT. Not one clearance, floor, cap or readout
 * NUMBER changed with that correction: minDrop, clearanceOK, clearanceGap,
 * maxTileDepth and the tvCabinet* readout fields were already built on the cabinet
 * and are untouched. The only thing that changed is which height the SCENE draws.
 * The rectangle stays centred on the drop (mid-rectangle = drop), because the split
 * is still even, so nothing about drop semantics moves either.
 *
 * [WARN] tvScreenHeight IS STILL HERE AND IS STILL EXPORTED. It is the pure 16:9
 * active-area helper and it is what the whole cabinet figure is derived FROM, so a
 * "cleanup" that folds the allowance into it would be the same silent inch this file
 * has warned about since the allowance landed: every derived number would gain an
 * inch instead of the cabinet gaining it once. It is simply no longer the thing that
 * gets drawn.
 *
 * MIN_CLEARANCE IS ABOUT INCREMENTS, AND IT IS STILL NOT THE BEZEL ALLOWANCE
 * These are two separate things and the arrival of the bezel allowance above makes
 * it easy to conflate them. The bezel allowance is a HEIGHT: it says the box is an
 * inch taller than the picture. MIN_CLEARANCE is a THRESHOLD on the gap that is
 * left over, and its justification has nothing to do with TVs at all.
 * Owner refined it 2026-07-26 and the reasoning matters, because it is not the
 * fudge factor it first looks like. Our adjustable masts adjust in ONE INCH steps,
 * so a finished drop cannot be trimmed finer than an inch. Landing the top of the
 * TV a few thousandths under the tile line is therefore not a position you can
 * dial out: the next thing you can actually do is drop a whole inch.
 * So the rule is a threshold plus a whole-increment bump, not a margin:
 *   gap = cabinet top depth - tile depth
 *   gap <  MIN_CLEARANCE (0.125 in)  ->  go one full inch lower
 *   gap >= MIN_CLEARANCE             ->  fine, leave it alone
 * A gap of an eighth of an inch or more is acceptable and is NOT rounded up. That
 * is the owner's call and it is deliberate: the point is to avoid an unachievable
 * position, not to buy safety margin.
 *
 * [WARN] THE BOUNDARY IS INCLUSIVE, and that is a deliberate correction to the
 * owner's literal wording. He said "within .125, add an inch, but .126 is fine",
 * which reads as bumping at exactly 0.125. Doing that makes the two rules in this
 * file contradict each other, because minDrop's floor lands a drop at a gap of
 * EXACTLY 0.125: the UI would clamp you to the stop, immediately bump you an inch,
 * and bump you again every time you dragged back. Caught by
 * scripts/test_vms_elevation.js. Both rules therefore derive from ONE predicate,
 * clearanceOK() below, with 0.125 accepted. The physical difference between a
 * 0.125 and a 0.126 gap is a thousandth of an inch and no mast can resolve it, so
 * nothing real is lost. Flagged to the owner rather than buried.
 *
 * FUTURE, owner-flagged 2026-07-26 (do NOT build unprompted): a "pro mode" that
 * asks for the real TV height, the distance from the top of the TV down to the
 * upper mounting holes, and the distance from the upper to the lower mounting
 * holes. That would replace the ESTIMATE with measured numbers AND fix the
 * assumption that the hole pattern is vertically centred on the screen, which is
 * the weak point in the model below (see tvTopDepth).
 * [WARN] The bezel allowance REDUCES the case for pro mode but does not remove it.
 * Two errors used to stack in a printed figure: the screen understated the box, and
 * the hole pattern is assumed centred. The first is now allowed for. The second is
 * untouched, because an even split leaves the mount reference exactly where it was.
 * Everything here is written so pro mode can drop in by supplying measured numbers
 * instead of derived ones.
 */
(function () {
  'use strict';

  // Exact 16:9 factor: 9 / sqrt(16^2 + 9^2).
  var TV_HEIGHT_FACTOR = 9 / Math.sqrt(16 * 16 + 9 * 9);   // 0.490261
  var TV_WIDTH_FACTOR = 16 / Math.sqrt(16 * 16 + 9 * 9);   // 0.871576

  // How much taller the physical TV is than its 16:9 screen, in inches. The bezel
  // sits outside the diagonal measurement, so it is outside the 16:9 arithmetic too.
  // OWNER RULING 2026-07-27, researched and FINAL: 1 inch. Split evenly above and
  // below, implemented 2026-07-28. Settled; do not re-derive it and do not
  // "improve" it. See the header.
  var TV_BEZEL_ALLOWANCE = 1;

  // Drawn thickness of a FACE-DOWN (horizontal) TV, in inches, seen edge on. A
  // dental head holds the TV flat, so the elevation draws it as a thin box whose
  // vertical extent is this fixed number at every screen size, while its drawn
  // WIDTH is the screen height-with-allowance. Owner ruling 2026-08-26: a flat
  // 2.5 in for every size. Only the horizontal path reads it; vertical heads are
  // untouched.
  var HORIZONTAL_TV_THICKNESS = 2.5;

  // Smallest gap we will leave between the top of the TV and the tile line.
  // A gap at or under this is not a position you can dial out, because the masts
  // adjust in whole inches, so it gets bumped a full increment instead. Applies to
  // the CLEARANCE RULE only, never to the drawn TV. See the header.
  var MIN_CLEARANCE = 0.125;

  // Adjustment granularity of the telescoping masts, in inches. The bump size.
  var ADJUST_INCREMENT = 1;

  // How close two constraint floors have to be before minDrop calls them level.
  // Purely a float-dust tolerance, four orders of magnitude below anything a mast
  // can resolve, and it never changes a reported number. See consider() below.
  var TIE_EPS = 1e-9;

  function num(v) { return typeof v === 'number' && !isNaN(v) ? v : null; }

  /** Screen height in inches from a diagonal, or null if no size given. */
  function tvScreenHeight(diagonal) {
    var d = num(diagonal);
    return d && d > 0 ? d * TV_HEIGHT_FACTOR : null;
  }

  /** Screen width in inches from a diagonal, or null. */
  function tvScreenWidth(diagonal) {
    var d = num(diagonal);
    return d && d > 0 ? d * TV_WIDTH_FACTOR : null;
  }

  /**
   * PHYSICAL TV height in inches from a diagonal: the cabinet, bezel included.
   * Null on an unusable size, exactly like tvScreenHeight, and deliberately NOT a
   * bare TV_BEZEL_ALLOWANCE: an unknown TV is not a 1 in TV.
   *
   * THIS is the height every clearance rule uses AND, since the owner's
   * 2026-07-28 overnight correction, the height the drawing draws (sceneModel's
   * drawn fields read tvCabinetHeight/tvCabinetWidth). tvScreenHeight remains
   * the pure 16:9 active-area helper only; nothing draws it any more.
   * Keeping them separate is the whole point of the owner's 2026-07-27 ruling; see
   * the header before collapsing them.
   *
   * [WARN] The allowance is added to the HEIGHT only. It must never also be added to
   * MIN_CLEARANCE or to a floor, which would move every clearance by a full inch
   * instead of the half inch an evenly split bezel actually costs. Tested.
   */
  function tvCabinetHeight(diagonal) {
    var h = tvScreenHeight(diagonal);
    return h == null ? null : h + TV_BEZEL_ALLOWANCE;
  }

  /**
   * Width of the DRAWN TV rectangle in inches: the cabinet height at 16:9.
   *
   * OWNER CORRECTION 2026-07-28, in his own words: draw "a 16:9 aspect ratio rectangle
   * 'screen'" from the inflated height. So this is deliberately NOT tvScreenWidth plus
   * some side allowance, and it is deliberately NOT the bare screen width: it is the
   * one rectangle the scene draws, and its height and width come from the same number.
   *
   * [WARN] IT IS WIDER THAN THE REAL SCREEN BY DESIGN. A 70 in TV draws 62.79 in wide
   * against a 61.01 in screen. That is the owner's ruling, not an error, and the extra
   * width costs nothing in the drawing because the TV is allowed to run off both sides
   * of the stage and be cropped (see assets/vms-elevation-scene.js). Do not "correct"
   * it back to tvScreenWidth: the drawn rectangle would then stop being 16:9 and the
   * caption that names it would stop matching its own diagonal.
   */
  function tvCabinetWidth(diagonal) {
    var c = tvCabinetHeight(diagonal);
    return c == null ? null : c * (TV_WIDTH_FACTOR / TV_HEIGHT_FACTOR);
  }

  /**
   * Depth of the top of the TV screen, given the drop.
   *
   * ASSUMPTION, and it is the shakiest thing in this file: the mount hole pattern
   * is treated as vertically centred on the screen, so the screen top is half a
   * screen height above the drop. Real VESA patterns often sit ABOVE centre,
   * which is why the configurator's own copy hedges with "about mid-screen".
   * Owner is aware and ruled it a future refinement. Pro mode fixes it properly by
   * measuring top-of-TV to upper-holes directly.
   */
  function tvTopDepth(drop, diagonal) {
    var h = tvScreenHeight(diagonal);
    var d = num(drop);
    return (h == null || d == null) ? null : d - h / 2;
  }

  function tvBottomDepth(drop, diagonal) {
    var h = tvScreenHeight(diagonal);
    var d = num(drop);
    return (h == null || d == null) ? null : d + h / 2;
  }

  /**
   * Depth of the top and the bottom of the physical TV CABINET.
   *
   * The bezel allowance is split EVENLY (owner ruling 2026-07-28), so these are the
   * screen depths pushed out by half the allowance in each direction, and the middle
   * of the cabinet is still exactly `drop`. That equality is asserted in the test
   * suite rather than left as a comment, because it is what keeps the hole-pattern
   * assumption in tvTopDepth exactly as true (and exactly as shaky) as it was before.
   */
  function tvCabinetTopDepth(drop, diagonal) {
    var c = tvCabinetHeight(diagonal);
    var d = num(drop);
    return (c == null || d == null) ? null : d - c / 2;
  }

  function tvCabinetBottomDepth(drop, diagonal) {
    var c = tvCabinetHeight(diagonal);
    var d = num(drop);
    return (c == null || d == null) ? null : d + c / 2;
  }

  // TV placement (catalog or manual row): SPEC-THEME-tv-placement-2026-10-12.md, notes in assets/vms-tv-lookup.notes.md.
  var TV_DEPTH_IN = 2.5;
  function rnum(v) {
    if (typeof v === 'string') { if (v.trim() === '') return null; } else if (typeof v !== 'number') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }
  function tvPlacement(row, opts) {
    var o = opts || {}, r = row || null, h = r ? rnum(r.height_mm) : null;
    if (h == null || !(h > 0)) {
      var c = tvCabinetHeight(o.tvSize);
      if (c == null) return null;
      return { source: 'generic', panelHeight: c, panelWidth: tvCabinetWidth(o.tvSize), widthFrom: 'generic',
        panelDepth: TV_DEPTH_IN, depthFrom: 'assumed', placed: false, hasOffsets: false,
        vesaCentreFromTop: c / 2, vesaCentreFromBottom: c - c / 2, offsetFromCentre: 0,
        vesaFromTop: null, vesaHeight: null, screenSizeIn: null,
        tier: null, uncertaintyMm: null, modelNumber: null, manufacturer: null, catalogVersion: null };
    }
    var manual = r.source === 'manual', H = h / 25.4, w = rnum(r.width_mm), d = rnum(r.depth_mm);
    var vt = rnum(r.vesa_from_top_mm), vh = rnum(r.vesa_height_mm), vb = rnum(r.vesa_from_bottom_mm);
    var cft = vt != null && vh != null ? (vt + vh / 2) / 25.4 : null;
    var placed = cft != null && cft > 0 && cft < H;
    var top = placed ? cft : H / 2;
    var pass = function (k) { return manual || r[k] == null ? null : r[k]; };
    return { source: manual ? 'manual' : 'catalog', panelHeight: H,
      panelWidth: w != null && w > 0 ? w / 25.4 : H * (TV_WIDTH_FACTOR / TV_HEIGHT_FACTOR), widthFrom: w != null && w > 0 ? 'catalog' : '16:9',
      panelDepth: d != null && d > 0 ? d / 25.4 : TV_DEPTH_IN, depthFrom: d != null && d > 0 ? 'catalog' : 'assumed',
      placed: placed, hasOffsets: vt != null && vb != null,
      vesaCentreFromTop: top, vesaCentreFromBottom: H - top, offsetFromCentre: placed ? top - H / 2 : 0,
      vesaFromTop: vt == null ? null : vt / 25.4, vesaHeight: vh == null ? null : vh / 25.4, screenSizeIn: manual ? null : rnum(r.screen_size_in),
      tier: r.offset_tier == null ? null : r.offset_tier, uncertaintyMm: r.offset_uncertainty_mm == null ? null : r.offset_uncertainty_mm,
      modelNumber: pass('model_number'), manufacturer: pass('manufacturer'), catalogVersion: pass('catalog_version') };
  }
  function tvOf(o) {
    if (!o.horizontal && o.tv && typeof o.tv.vesaCentreFromTop === 'number') return o.tv;
    return tvPlacement(null, { tvSize: o.tvSize });
  }

  /**
   * The smallest drop that is physically allowed, and WHY, so the UI can explain
   * its own hard stop instead of just refusing to move.
   *
   * IT IS THE MAXIMUM OF EVERY CONSTRAINT THAT APPLIES, not the first one found.
   * That is the whole shape of this function and it was NOT its shape until
   * 2026-07-27. Before that it answered the drop-ceiling question and nothing
   * else, so `{min: 0, reason: 'none'}` came back for every build with no drop
   * ceiling, and the caller floored that at a hardcoded 1 in. The owner found the
   * consequence with the drag: a 70 in screen at a 1 in drop draws its top at
   * 1 - 17.159 = -16.159 in, sixteen inches ABOVE the building structure it hangs
   * from. Two of the four constraints below are the fix, and both of them apply
   * whether or not there is a drop ceiling.
   *
   * The constraints, in the order they are considered:
   *
   * 1. BUILDABLE, reason 'shortestMast'. The mast has a shortest possible length,
   *    and mastTarget = drop - plateOffset - headOffset, so the shortest finished
   *    drop is plateOffset + headOffset + minMastLength. minMastLength is an
   *    INPUT, never invented here: the caller derives it from the same pipe pools
   *    the solver builds masts from, so the drawing and the solver cannot
   *    disagree about what exists.
   *    [WARN] headOffset can be NEGATIVE. 33CE004HEAD is -5.126 and reaches back
   *    UP toward the ceiling. The floor is a plain SUM and the sign carries
   *    itself: 3.126 + (-5.126) + 2 = 0, which is a REAL floor of zero, not a
   *    missing one. Never write this as pOff - hOff, and never take an absolute
   *    value; either one puts the floor 10 in too deep on that head. Tested.
   *
   * 2. PHYSICAL, reason 'screenClearsPlate'. The TV cannot rise into the CEILING
   *    it hangs from. The top of the physical TV may come up to within
   *    MIN_CLEARANCE of the building structure at depth 0:
   *    drop - C/2 >= clearance, i.e. drop >= C/2 + clearance. Applies only when a
   *    TV size is known, which is the second reason a size is worth prompting for.
   *
   *    [WARN] THE CODE NAME IS HISTORICAL AND MUST NOT BE RENAMED. It once meant
   *    literally what it says, "the screen clears the plate". Since the owner
   *    ruling of 2026-07-28 (evening) the constraint is TV-CLEARS-CEILING and the
   *    plate is not in it at all. The string stays because it is a cross-file API:
   *    assets/vms-configurator.js (heldBecause) and assets/vms-elevation-scene.js
   *    (reasonLines) both switch on it, and THREE SHIPPED THEME SETTING IDS are
   *    derived from it - copy_js_note_screen_clears_plate,
   *    copy_js_held_screen_clears_plate and copy_js_limit_high_plate - which hold
   *    the owner's live copy edits. Renaming any of that silently drops a copy
   *    branch into the fallback on whichever surface got missed AND orphans the
   *    owner's stored wording. If it is ever renamed, the setting ids have to be
   *    migrated in the same change. What it measures: the physical box, bezel
   *    included, against the ceiling. The customer-facing wording says TV.
   *
   *    [WARN] THE PLATE DELIBERATELY DOES NOT PARTICIPATE, and this REVERSES the
   *    tightening of 2026-07-27. That version read drop >= plateOffset + C/2, on
   *    the reasoning that the plate hangs directly above the TV. The reasoning was
   *    wrong about the physical object: the TV hangs IN FRONT of the plate, not
   *    underneath it, so the plate is not an obstacle. Owner ruling 2026-07-28
   *    evening, and it is forward-looking as well as correct - an angled ceiling
   *    plate is planned, on which the top of the TV can legitimately sit ABOVE the
   *    plate. Never reintroduce a plate-relative term here.
   *    A visible consequence, and it is expected: at this floor the drawn TV
   *    OVERLAPS the drawn ceiling plate. The scene must render the TV IN FRONT.
   *
   *    [WARN] THE CLEARANCE IS THE SAME 0.125 THE TILE LINE USES, and for the same
   *    recorded reason, which is nothing to do with bezels: the masts adjust in
   *    whole inches, so a hair of clearance is not a position you can dial out.
   *    See MIN_CLEARANCE's section in the header.
   *    Note what this rule now IS: branch 3 below with the tile line taken at the
   *    structure. So it can never beat a real tile floor, and at a tile depth of
   *    exactly 0 the two TIE and the tiles take the reason. Tested both ways.
   *
   * 3. THE DROP CEILING. The TV has to hang clear below the tile line: the mast
   *    comes down from the real structure, passes through a tile, and the TV lives
   *    in the room. So the TV top must be at or below the tiles:
   *    drop - C/2 >= tileDepth + allowance, i.e.
   *    drop >= tileDepth + C/2 + allowance, reason 'tvClearsTiles'. With no TV
   *    size we cannot know C, so it falls back to keeping the mount point itself
   *    below the tiles, reason 'belowTiles'; weaker but never wrong.
   *    C rather than H since 2026-07-28, which moved this floor half an inch
   *    deeper. clearanceGap() below measures the SAME cabinet top, deliberately:
   *    if this floor were built on the cabinet while the gap were measured on the
   *    screen, the clamp would land on a value the increment bump immediately
   *    moves, which is the boundary contradiction the file header warns about.
   *
   * 4. HORIZONTAL (FACE-DOWN) HEADS, owner ruling 2026-09-22 (DD-HEAD). The TV
   *    hangs flat under the VESA disc, so no TV edge can reach the ceiling or the
   *    tiles; the thing that can is the HEAD ITSELF, whose top sits headTopIn
   *    above the disc (opts.headTopIn, 3.82 in for DD-HEAD, from
   *    HEAD_TOP_ABOVE_VESA_IN in assets/vms-configurator.js). Branches 2 and 3's
   *    TV rules are replaced for such a head by:
   *      reason 'headClearsStructure': drop >= headTopIn + clearance, the head top
   *        within 1/8 in of the structure at depth 0 (with no drop ceiling the
   *        shortest mast is the practical limit and this never binds today, but
   *        the rule exists);
   *      reason 'headClearsTiles': drop >= tileDepth + headTopIn + clearance, the
   *        head top 1/8 in below the tile underside when a drop ceiling is on.
   *    The ceilingNoDepth ask applies to a horizontal head exactly as to a
   *    vertical one. A horizontal head WITHOUT a usable headTopIn is a
   *    configuration error and falls back to the VERTICAL rules above (never to
   *    no rule); the caller warns about it.
   *
   * The baseline is 0, which IS the pre-existing "a drop is more than zero" rule
   * (a plate and a head are never threaded straight together). A candidate has to
   * beat it to be reported, which is also why a floor can never come back
   * negative even though a deep enough negative head offset makes the raw
   * arithmetic negative.
   *
   * TIES GO TO THE LATER CONSTRAINT (the comparison is >=, not >), so the order
   * above is a deliberate priority, lowest first. Where a parts floor and a tile
   * floor land on the same number the customer hears about the tiles, because
   * that is the surprising one and it is the one with the specific copy. Ties are
   * vanishingly rare in real numbers but the rule should not be an accident of
   * evaluation order.
   *
   * `reason` is deliberately ONE string naming the constraint that actually
   * produced the maximum, because it is what the UI says out loud at the stop.
   *
   * [WARN] WHICH IS WHY THE OUTSTANDING TILE DEPTH GETS ITS OWN FLAG. The
   * 'ceilingNoDepth' branch contributes a floor of ZERO, so the moment any other
   * constraint binds it loses the max and stops being the reason. That is correct
   * for `reason` and fatal for anyone who was reading `reason === 'ceilingNoDepth'`
   * as "we still need the tile depth": on the feature's DEFAULT path (customer
   * picks "yes, drop ceiling", has not typed a depth yet, and has entered a TV
   * size) reason is now 'screenClearsPlate' and the request for the depth would
   * silently disappear. `needsTileDepth` states it separately and is true whenever
   * a drop ceiling is declared without a usable depth, whatever else is binding.
   * Never word it as "no drop ceiling"; it is a request for the distance from the
   * structure down to the tiles.
   *
   * reason 'none' survives but should now be RARE: it means no constraint we can
   * evaluate applies, which needs no drop ceiling declared, no TV size, AND an
   * incomplete parts picture. On the wired path in the configurator it is
   * unreachable, because minMastLength is a module constant and step 4 cannot be
   * reached without a plate and a head. It is kept for partial-state callers (the
   * scene draws before a build is complete) and because deleting it would mean
   * inventing a floor out of nothing, which is exactly what this module refuses to
   * do everywhere else.
   *
   * @returns {{min:number, reason:string, needsTileDepth:boolean}}
   */
  function minDrop(opts) {
    opts = opts || {};
    var tile = num(opts.tileDepth);
    // [RULE] HORIZONTAL (FACE-DOWN) HEADS, owner ruling 2026-08-26. A dental head
    // holds the TV flat, so the tall-screen floors below do NOT apply: there is no
    // screen top to meet the ceiling and no top corner to clear the tiles. Only the
    // buildable 'shortestMast' floor survives (you still cannot build a mast shorter
    // than the shortest pipe). Every vertical head keeps all of its floors. The
    // caller passes this straight from head.horizontal; see HORIZONTAL_HEADS in
    // assets/vms-configurator.js, the one place orientation is decided.
    var horizontal = !!opts.horizontal;
    // [RULE, 2026-09-22] HEAD TOP ABOVE THE VESA DISC, horizontal heads only. With
    // it known, a face-down head gets its own two floors (header, constraint 4):
    // the head top clears the structure and, under a drop ceiling, the tiles. The
    // 2026-08-26 skip above left NO tile floor at all, so the head could rise
    // through the tiles. Without it the head falls back to the vertical rules.
    var headTop = num(opts.headTopIn);
    var headRule = horizontal && headTop != null;
    if (horizontal && headTop == null) horizontal = false;
    // THE CABINET, NOT THE SCREEN. Every floor below is a CLEARANCE, and a bezel
    // collides with a ceiling plate just as solidly as a picture does. The drawn
    // screen height is not this function's business. Owner ruling 2026-07-27.
    var P = tvOf(opts);
    var clear = opts.minClearance == null ? MIN_CLEARANCE : opts.minClearance;
    var pOff = num(opts.plateOffset);
    var hOff = num(opts.headOffset);
    var minMast = num(opts.minMastLength);

    var min = 0, reason = 'none';

    // A missing input SKIPS its constraint. It never contributes a guessed zero,
    // which would read as "no floor here" and is how a silent hard stop gets
    // built. num() rejects numeric STRINGS, so a caller reading a dataset or a
    // URLSearchParams has to Number() first or its constraint quietly vanishes.
    //
    // `min` only ever RISES. The tie test carries an epsilon and the reason test
    // does not, which is two separate jobs in three lines:
    //   - a candidate below the running maximum cannot bind, so it is dropped;
    //   - a candidate level with it takes the REASON but changes no number.
    // [WARN] The epsilon is load-bearing, not decoration. These floors are sums of
    // catalog decimals: 3.126 + (-5.126) + 2 for the negative-offset head is not
    // 0 in binary floating point, it is about -4.4e-16. Without the tolerance that
    // real parts floor lost a tie with the zero baseline and came back as
    // reason 'none', i.e. "nothing is constraining this build" at the exact drop
    // where the shortest mast we sell runs out. Caught by the negative-offset test.
    function consider(candidate, why) {
      if (candidate == null) return;
      if (candidate < min - TIE_EPS) return;   // a lower floor cannot bind
      if (candidate > min) min = candidate;
      reason = why;
    }

    // 1. Buildable. All three parts of the sum or none of it: two thirds of a
    // finished drop is not a floor, it is a wrong number.
    if (pOff != null && hOff != null && minMast != null && minMast >= 0) {
      consider(pOff + hOff + minMast, 'shortestMast');
    }

    // 2. Physical. Applies with or without a drop ceiling. The top of the physical
    // TV may come up to within `clear` of the building structure at depth 0.
    //
    // [WARN] THE NAME 'screenClearsPlate' IS HISTORICAL AND THE PLATE IS NOT IN
    // THIS LINE. It is a cross-file API string with three shipped theme setting ids
    // derived from it; renaming it breaks the owner's live copy edits. Full note in
    // this function's header, and do not add a plateOffset term back.
    if (!horizontal) consider(P == null ? null : P.vesaCentreFromTop + clear, 'screenClearsPlate');
    // The horizontal counterpart: the HEAD top may come within `clear` of the
    // structure. The shortest mast is larger for every head today, so it does not
    // bind, but the rule has to exist rather than be assumed.
    if (headRule) consider(headTop + clear, 'headClearsStructure');

    // 3. The drop ceiling. Vertical heads exactly as before; a horizontal head with
    // a known head top runs it too, measuring the head top instead of the TV.
    var needsTileDepth = false;
    // (horizontal is already false here when the head top is unknown, see above.)
    if (opts.hasDropCeiling) {
      if (tile == null || tile < 0) {
        // [WARN] Drop ceiling declared but the depth is missing or nonsense. This
        // used to collapse into 'none', which adversarial review on 2026-07-26
        // found to be a LIE with teeth: it is the DEFAULT state of the feature, so
        // the UI would have said "no drop ceiling" and applied no clamp at all on
        // the single most common path. A negative depth lands here too rather than
        // being silently trusted. See the flag warning in the header: it applies no
        // clamp, so its zero is masked by any other binding constraint, which is
        // why needsTileDepth exists.
        needsTileDepth = true;
        consider(0, 'ceilingNoDepth');
      } else if (headRule) {
        // The head top 1/8 in under the tile underside (owner 2026-09-22). No TV
        // term: the TV hangs flat below the disc and cannot reach the tiles.
        consider(tile + headTop + clear, 'headClearsTiles');
      } else if (P == null) {
        // Depth known, TV size not: keep the mount point itself below the tiles.
        consider(tile, 'belowTiles');
      } else {
        consider(tile + P.vesaCentreFromTop + clear, 'tvClearsTiles');
      }
    }

    return { min: min, reason: reason, needsTileDepth: needsTileDepth };
  }

  /**
   * Gap in inches between the top of the physical TV and the tile line. Positive
   * means the TV hangs clear below the tiles, which is what we want. Null when there
   * is no drop ceiling or no TV size, i.e. when the question does not apply.
   *
   * [WARN] MEASURED ON THE CABINET TOP, and it MUST stay in step with minDrop's
   * tvClearsTiles branch, which is this same rule stated backwards. Both moved to
   * the cabinet together on 2026-07-28. Move one back to the screen on its own and
   * the two disagree by half an inch: minDrop clamps the customer to a floor, this
   * gap reports it as short, resolveDrop bumps a whole inch, and it re-bumps every
   * time they drag back to the stop. That is the exact contradiction the header's
   * boundary warning is about, re-armed by a different route.
   */
  function clearanceGap(drop, opts) {
    opts = opts || {};
    if (!opts.hasDropCeiling) return null;
    var tile = num(opts.tileDepth);
    // [RULE, 2026-09-22] A HORIZONTAL head with a known head top measures the HEAD
    // top, the same thing minDrop's headClearsTiles branch floors, so the floor and
    // this gap stay one rule stated two ways (see the warning above). Measuring the
    // vertical cabinet here bumped the owner's DD-HEAD build from 20 to 21.
    var headTop = num(opts.headTopIn);
    var d = num(drop);
    var P = tvOf(opts);
    var top = (opts.horizontal && headTop != null)
      ? (d == null ? null : d - headTop)
      : (d == null || P == null ? null : d - P.vesaCentreFromTop);
    return (tile == null || top == null) ? null : top - tile;
  }

  /**
   * THE single acceptance predicate. minDrop's floor and resolveDrop's bump are both
   * derived from this, so they can never disagree at the boundary. A null gap means
   * the question does not apply (no drop ceiling, or no TV size), which is always
   * acceptable. See the boundary warning in the file header.
   *
   * [WARN] THE EPSILON IS NOT DECORATION, and its absence was a live defect found by
   * adversarial review on 2026-07-28. minDrop builds the tile floor as
   * tile + C/2 + clear. clearanceGap then takes it back apart as (drop - C/2) - tile.
   * That round trip does not return `clear` in binary floating point: at a 55 in TV
   * under 3.5 in of tile it returns 0.12499999999999822, which is 1.8e-15 short, and
   * a bare >= rejected it. So minDrop's own floor failed minDrop's own predicate, and
   * resolveDrop moved the customer a WHOLE INCH off a stop they had just been clamped
   * to, announcing a tile collision that was not happening. 1124 size-and-tile
   * combinations did it, and only on the TYPED path: the drag and the number field
   * both reach the stop by routes that do not re-derive the gap, so the same build
   * gave two different answers to "how high can it go" depending on which input the
   * customer used. See the FLOOR MUST NEVER BUMP section in the test suite.
   *
   * TIE_EPS is 1e-9 against a threshold of 0.125, six orders of magnitude apart. It
   * cannot swallow a real shortfall: the smallest one that matters is a thousandth of
   * an inch, which no mast can resolve either. Do not remove it, and do not "simplify"
   * it to a bare >=, which is what it looked like for months.
   */
  function clearanceOK(gap, minClearance) {
    if (gap == null) return true;
    var clear = minClearance == null ? MIN_CLEARANCE : minClearance;
    return gap >= clear - TIE_EPS;
  }

  /**
   * Apply the increment rule to a drop. Returns the drop to actually use, whether
   * it was bumped, and the gap before and after, so the UI can SAY why it moved
   * rather than silently shifting the customer's number by an inch.
   *
   * Only ever moves DOWN (a longer drop, screen lower), never up: raising the TV
   * toward the tiles is the thing we are avoiding.
   *
   * IDEMPOTENT, and it takes AS MANY WHOLE INCREMENTS AS IT NEEDS to get there.
   *
   * [WARN] It originally added exactly one increment, which was a real bug found by
   * adversarial review on 2026-07-26. One inch only clears the threshold when the gap
   * is already within an inch of it. Feed it a drop well ABOVE the tile line (a large
   * negative gap) and every call bumped again: resolveDrop(20) gave 21, then 22, then
   * 23, unbounded. The comment here claimed idempotency and the test suite had no
   * negative-gap case at all, so nothing caught it. Because the drag calls this
   * continuously, that is a value creeping down an inch per pointermove, which is
   * exactly the silently-creeping-value defect SPEC-drag-elevation-2026-07-26.md
   * condemns Sanus for. Ceiling division fixes it by construction: the result always
   * satisfies clearanceOK, so a second call is always a no-op, for every input.
   * There are now negative-gap tests. Do not "simplify" this back to `d + step`.
   *
   * [WARN] THAT SENTENCE WAS ONLY TRUE OF THE SECOND CALL, and it read as though it
   * covered the FIRST. Adversarial review on 2026-07-28 found the gap: idempotency
   * held, but the value it settled on could be an inch below where it should have
   * started, because clearanceOK rejected minDrop's own floor over float dust and
   * this function dutifully bumped it. Both properties are needed and they are not
   * the same property. clearanceOK now carries the epsilon that makes the first one
   * true; see its header. If that epsilon is ever removed, this comment goes false
   * again and nothing here will catch it.
   */
  function resolveDrop(drop, opts) {
    opts = opts || {};
    var d = num(drop);
    if (d == null) return { drop: null, bumped: false, gap: null, gapBefore: null };
    var step = opts.increment == null ? ADJUST_INCREMENT : opts.increment;
    var clear = opts.minClearance == null ? MIN_CLEARANCE : opts.minClearance;
    var gap = clearanceGap(d, opts);
    if (clearanceOK(gap, opts.minClearance)) {
      return { drop: d, bumped: false, gap: gap, gapBefore: gap };
    }
    // Whole increments only, because that is what the masts can actually do, but as
    // many as the shortfall requires. A tiny shortfall still costs exactly one inch,
    // which is the owner's rule; a large one is not silently under-corrected.
    var steps = Math.ceil((clear - gap) / step);
    if (!(steps > 0)) steps = 1;                 // guards a zero/NaN step
    var bumped = d + steps * step;
    return { drop: bumped, bumped: true, gap: clearanceGap(bumped, opts), gapBefore: gap, steps: steps };
  }

  /**
   * Everything the scene needs, as depths in inches from the structure. Returns
   * nulls rather than guesses for anything not yet known, so a renderer can draw a
   * partial scene (structure and tile line before a head is picked, say) instead of
   * waiting for a complete build.
   */
  function sceneModel(opts) {
    opts = opts || {};
    var drop = num(opts.drop);
    var pOff = num(opts.plateOffset);
    var hOff = num(opts.headOffset);
    var horizontal = !!opts.horizontal;
    // THE DRAWN HEIGHT, and since the owner's correction of 2026-07-28 that is the
    // CABINET, not the screen. See the header. The drawn rectangle is 16:9 off this
    // same number, so the picture and every clearance now agree at the stops.
    var c = tvCabinetHeight(opts.tvSize);
    var P = tvOf(opts);
    var tile = opts.hasDropCeiling ? num(opts.tileDepth) : null;
    // THE DRAWN TV BOX. A vertical head draws one 16:9 cabinet rectangle centred on
    // the drop. A HORIZONTAL (face-down) head draws a thin flat box seen edge on:
    // its drawn WIDTH is the cabinet height c (the TV height-with-allowance) and its
    // drawn thickness is the fixed HORIZONTAL_TV_THICKNESS, and its TOP edge (the
    // plate face, i.e. the back of the TV) sits AT the drop, so it hangs below the
    // drop rather than straddling it. Owner ruling 2026-08-26.
    // Face-down placed TV (spec 5.3; notes assets/vms-tv-lookup.notes.md): real height across, real depth down, shifted by tvShift.
    var F = horizontal && opts.tv && typeof opts.tv.panelHeight === 'number' ? opts.tv : null;
    var drawnH = horizontal ? (F ? F.panelDepth : HORIZONTAL_TV_THICKNESS) : (P ? P.panelHeight : null);
    var drawnW = horizontal ? (F ? F.panelHeight : c) : (P ? P.panelWidth : null);
    var drawnTop = horizontal ? drop : (P && drop != null ? drop - P.vesaCentreFromTop : null);
    var drawnBottom = horizontal
      ? (drop != null ? drop + drawnH : null)
      : (P && drop != null ? drop + P.vesaCentreFromBottom : null);

    // Pipe runs from the bottom of the plate to wherever the head starts. For a
    // NEGATIVE head offset the pipe end sits BELOW the mount point, because that
    // head reaches back up toward the ceiling: drop - (-5.126) is deeper than drop.
    var pipeTop = pOff;
    var pipeBottom = (drop != null && hOff != null) ? drop - hOff : null;

    // [WARN] EVERY minDrop INPUT HAS TO BE FORWARDED HERE, and the list grew on
    // 2026-07-27. Drop one and the scene draws a hard stop the number field does
    // not enforce, or worse, no stop where the field has one: the two would be
    // computing the same floor from different inputs. The combination sweep in
    // scripts/test_vms_elevation.js asserts model.minDrop equals a direct
    // minDrop call for every combination, so a forgotten key fails loudly.
    var lim = minDrop({
      hasDropCeiling: !!opts.hasDropCeiling,
      tileDepth: opts.tileDepth,
      tvSize: opts.tvSize,
      minClearance: opts.minClearance,
      plateOffset: opts.plateOffset,
      headOffset: opts.headOffset,
      minMastLength: opts.minMastLength,
      horizontal: horizontal,
      headTopIn: opts.headTopIn,
      tv: opts.tv
    });

    return {
      structureDepth: 0,
      plateBottom: pOff,
      tileDepth: tile,
      pipeTop: pipeTop,
      pipeBottom: pipeBottom,
      mastLength: (pipeTop != null && pipeBottom != null) ? pipeBottom - pipeTop : null,
      mountDepth: drop,                       // the hole pattern, and the drawn TV centre
      // THE DRAWN RECTANGLE. One 16:9 box off the CABINET height (owner correction
      // 2026-07-28, superseding the screen-drawn convention of earlier the same day).
      // Still centred on the drop, because the bezel allowance is still split evenly.
      horizontal: horizontal,
      tvHeight: drawnH,
      tvWidth: drawnW,
      tvTop: drawnTop,
      tvBottom: drawnBottom,
      // The same three numbers under their clearance names. The readout in
      // assets/vms-configurator.js reads THESE, and they were the cabinet before the
      // drawing was, so nothing about them changed: they are kept as a separate,
      // explicitly named surface because renaming a shipped published field to save
      // three lines would be a cross-file break for no gain.
      // For a horizontal head these mirror the flat box (thickness, back, face) so
      // the "where the TV lands" readout matches the drawing; a vertical head keeps
      // its cabinet clearance numbers unchanged (drawnTop/drawnBottom equal the
      // cabinet depths in that case). Owner ruling 2026-08-26.
      tvCabinetHeight: horizontal ? drawnH : (P ? P.panelHeight : null),
      tvCabinetTop: drawnTop,
      tvCabinetBottom: drawnBottom,
      // The NOMINAL diagonal, echoed back for display only. The scene captions the
      // drawn band with the TV size, and it used to derive that from the diagonal of
      // the drawn rectangle, which WAS the nominal size while the rectangle was the
      // pure 16:9 screen. A 16:9 rectangle built off the cabinet has a diagonal about
      // 2 in larger, so that derivation would now caption a 70 in TV as 72. Echoed
      // rather than recomputed so the caption cannot invent a size of its own.
      tvSize: num(opts.tvSize) != null && opts.tvSize > 0 ? num(opts.tvSize) : null,
      tvPlacement: F || P,
      tvShift: F ? F.offsetFromCentre : 0,
      tvTopEdge: F && (F.topEdge === 'left' || F.topEdge === 'right') ? F.topEdge : null,
      minDrop: lim.min,
      minDropReason: lim.reason,
      // Rides beside minDropReason rather than inside it, because the reason has
      // to name what is BINDING and this question can be outstanding while
      // something else binds. See the warning in minDrop's header.
      needsTileDepth: lim.needsTileDepth,
      // Deepest thing in the scene, so a renderer can pick ONE scale for all of it.
      //
      // [WARN] NULL, not zero, when nothing in the scene has a depth yet. It used to
      // return 0, which adversarial review on 2026-07-26 flagged as a divide-by-zero
      // waiting to happen: this is precisely the value a renderer divides a slot
      // height by to derive px-per-inch, and 0 gives Infinity rather than an obvious
      // failure. Null forces the caller to guard. Note minDrop can legitimately BE 0
      // (it means "no floor beyond the above-zero rule"), so do not conflate the two.
      //
      // [WARN] IT MEASURES WHAT IS DRAWN, AND WHAT IS DRAWN IS THE CABINET since the
      // owner's correction of 2026-07-28. It used to take the screen bottom and carry
      // a warning that the cabinet bottom was deliberately excluded because nothing
      // painted it; the drawing paints it now, so it belongs here and the scene
      // legitimately gets half an inch deeper wherever the TV binds the depth. The
      // rule itself is unchanged and is the reason this comment is worth keeping: put
      // ONLY extents that are actually painted in this list, because a renderer
      // divides a slot height by this number to get one px-per-inch, and reserving
      // room for something invisible rescales the whole drawing for nothing.
      sceneDepth: (function () {
        var known = [drop, drawnBottom, pipeBottom, tile]
          .filter(function (v) { return num(v) != null; });
        return known.length ? Math.max.apply(null, known) : null;
      })()
    };
  }

  /** Clamp a proposed drop to the physical minimum. Used by BOTH the drag and the number field. */
  function clampDrop(drop, opts) {
    var d = num(drop);
    if (d == null) return null;
    var lim = minDrop(opts);
    return d < lim.min ? lim.min : d;
  }

  /**
   * Round a value to the nearest multiple of `step`.
   *
   * THE ONE QUANTISER. There are three granularities loose in this feature and they
   * have to be reconciled to one: the number field carries step="0.5", the masts
   * adjust in whole inches (ADJUST_INCREMENT), and a minDrop floor like 35.784 is
   * unrounded. The contract is: the drag emits a RAW unrounded position, the caller
   * quantises it here exactly once, and every readout on screen derives from that
   * single number. Nothing downstream rounds again.
   *
   * [WARN] Do not add a second rounding step anywhere, however harmless it looks.
   * Two rounding grids is precisely how the Sanus tool ends up printing two numbers
   * for the same position that disagree by an eighth of an inch.
   *
   * Halfway values break toward +Infinity, because that is what Math.round does and
   * inventing our own tie rule would be one more thing to remember. Unusable input
   * gives null rather than a guess, the same discipline as num() everywhere else in
   * this file: a numeric STRING is unusable, so anything off a dataset or a
   * URLSearchParams must be Number()d by the caller first.
   */
  function quantise(value, step) {
    var v = num(value), s = num(step);
    if (v == null || s == null) return null;
    if (!isFinite(v) || !isFinite(s) || s <= 0) return null;
    var r = Math.round(v / s) * s;
    return r === 0 ? 0 : r;          // never hand back -0
  }

  /**
   * Round a minimum UP to the next multiple of `step`, so a clamp floor can be
   * written into a number input without tripping its own step attribute.
   *
   * minDrop returns real geometry: 35.784 in for a 70 in TV under 18 in of tile
   * depth. Put that straight into an input with step="0.5" and the field reports
   * stepMismatch and paints itself :invalid, which reads to the customer as "you
   * typed something wrong" when the number came from us.
   *
   * [WARN] It MUST round up, never to nearest. minDrop returns 22.558490288058167
   * in for a 65 in TV under 6 in of tile depth, and rounding that to nearest on the
   * 0.5 in step grid gives 22.5, which sits BELOW the real minimum and quietly
   * reopens the exact hole the clamp exists to close. A floor that sits under the
   * value is not a floor. The overshoot is at most one step, which is a shade under
   * half an inch of extra caution and always in the safe direction.
   */
  function fieldFloor(minDropValue, step) {
    var v = num(minDropValue), s = num(step);
    if (v == null || s == null) return null;
    if (!isFinite(v) || !isFinite(s) || s <= 0) return null;
    var r = Math.ceil(v / s) * s;
    // Float dust guard. Ceiling division is exact for a 0.5 grid, but a caller can
    // pass any step, and a result one ULP under the input would break the one
    // property this function exists to guarantee. Costs one comparison.
    if (r < v) r = r + s;
    return r === 0 ? 0 : r;
  }

  /**
   * The deepest tile depth that still leaves a LEGAL drop, in inches.
   *
   * This is the answer to "how far below the structure can a drop ceiling sit and
   * still be something we can build for?", and it is DERIVED rather than chosen.
   * Until 2026-07-27 the tile field carried a flat max of 60 in and nothing had
   * ever justified it. Real drop ceilings routinely hang further than that below
   * the structure (warehouses, gyms, big-box retail, atriums), so that cap sent
   * those customers to the phone over a number nobody picked, while the drop field
   * beside it accepted 200.
   *
   * The reasoning, and it is the whole function:
   *   1. The drop has its own maximum, which the CALLER owns and passes in. It is
   *      the outer bound of the tool, not of the tiles.
   *   2. The TV has height, and it has to hang clear BELOW the tiles, so the
   *      drop that a given tile depth demands is tileDepth + C/2 + clearance
   *      (C the TV cabinet height). That is minDrop's tvClearsTiles branch,
   *      stated backwards.
   *   3. Set that demand equal to the drop maximum and solve for the tile depth:
   *        maxTile = dropMax - C/2 - clearance
   *      For a 70 in screen against a 200 in drop maximum that is 182.216 in, and
   *      it MOVES with the screen size. That is the point: the limit a customer
   *      hits should be one the geometry imposes, not one we invented.
   *
   * `dropMax` is an INPUT, never a constant in here, for the same reason
   * minMastLength is: the field's own maximum lives in the configurator and the
   * drawing must not be able to disagree with it.
   *
   * [WARN] WITH NO USABLE TV SIZE THIS FALLS BACK TO THE PLAIN OUTER BOUND, and it
   * must never guess a TV height to tighten it. That is the same discipline as
   * minDrop's belowTiles branch: weaker, but never wrong. Note the direction of the
   * risk, because it is the opposite of everywhere else in this file: num() rejects
   * numeric STRINGS, so a tvSize read straight off a dataset or a URLSearchParams
   * silently LOOSENS this cap instead of tightening it. Number() first.
   *
   * [WARN] IT CAN NEVER RETURN MORE THAN dropMax, and never less than 0. A cap
   * above the bound would advertise a tile depth whose required drop is
   * unreachable; a negative cap would be a stop the tile handle cannot reach. A
   * result of 0 is a real answer meaning no tile depth works at this screen size,
   * not a missing one, and unusable INPUT still gives null rather than a guess.
   */
  function maxTileDepth(dropMax, diagonal, minClearance, opts) {
    var m = num(dropMax);
    if (m == null || !isFinite(m) || m < 0) return null;
    // [RULE, 2026-09-22] A HORIZONTAL head with a known head top: the inverse of
    // minDrop's headClearsTiles floor, so the deepest tile still leaves the head top
    // `clear` below it at dropMax. The TV size plays no part. `opts` is optional
    // and every older three-argument call is unchanged.
    var o = opts || {};
    var headTop = num(o.headTopIn);
    if (o.horizontal && headTop != null) {
      var hclear = minClearance == null ? MIN_CLEARANCE : minClearance;
      var hcap = m - headTop - hclear;
      return hcap > 0 ? hcap : 0;
    }
    // The CABINET, for the same reason minDrop uses it: this is that same clearance
    // rule solved for the tile depth instead of for the drop, so the two have to be
    // built from the same height or the cap stops being the inverse of the floor.
    var P = tvOf({ horizontal: o.horizontal, tv: o.tv, tvSize: diagonal });
    if (P == null) return m;                 // no size known: the plain outer bound
    var clear = minClearance == null ? MIN_CLEARANCE : minClearance;
    var cap = m - P.vesaCentreFromTop - clear;
    return cap > 0 ? cap : 0;
  }

  /**
   * Round a maximum DOWN to the next multiple of `step`. The mirror of fieldFloor,
   * and it exists for the same reason in reverse.
   *
   * maxTileDepth returns real geometry: 182.216 in for a 70 in screen. Write that
   * into an input with step="0.5" and the field reports stepMismatch and paints
   * itself :invalid, exactly as an unrounded minimum does.
   *
   * [WARN] It MUST round down, never to nearest. maxTileDepth returns
   * 183.44150971194182 in for a 65 in screen, and rounding that to nearest on the
   * 0.5 in step grid gives 183.5, which puts the field's cap ABOVE the real one: a
   * tile depth of 183.5 demands a drop of 200.058, so the drop field would then be
   * handed a min above its own max and would be invalid whatever the customer
   * typed. That same class of failure was found in the browser on 2026-07-27 by a
   * different route (the tile commit path was dead code and left #target on min
   * 1017 against max 200), which is why it is a tested property here rather than
   * an argument. A cap that sits above the value is not a cap. The undershoot is
   * at most one step and always in the safe direction.
   */
  function fieldCap(maxValue, step) {
    var v = num(maxValue), s = num(step);
    if (v == null || s == null) return null;
    if (!isFinite(v) || !isFinite(s) || s <= 0) return null;
    var r = Math.floor(v / s) * s;
    // Float dust guard, same as fieldFloor's and for the same one property.
    if (r > v) r = r - s;
    return r === 0 ? 0 : r;
  }

  window.VMSElevation = {
    TV_HEIGHT_FACTOR: TV_HEIGHT_FACTOR,
    TV_WIDTH_FACTOR: TV_WIDTH_FACTOR,
    MIN_CLEARANCE: MIN_CLEARANCE,
    ADJUST_INCREMENT: ADJUST_INCREMENT,
    TV_BEZEL_ALLOWANCE: TV_BEZEL_ALLOWANCE,
    HORIZONTAL_TV_THICKNESS: HORIZONTAL_TV_THICKNESS,
    tvScreenHeight: tvScreenHeight,
    tvScreenWidth: tvScreenWidth,
    tvCabinetHeight: tvCabinetHeight,
    tvCabinetWidth: tvCabinetWidth,
    tvTopDepth: tvTopDepth,
    tvBottomDepth: tvBottomDepth,
    tvCabinetTopDepth: tvCabinetTopDepth,
    tvCabinetBottomDepth: tvCabinetBottomDepth,
    minDrop: minDrop,
    clearanceGap: clearanceGap,
    clearanceOK: clearanceOK,
    resolveDrop: resolveDrop,
    clampDrop: clampDrop,
    sceneModel: sceneModel,
    quantise: quantise,
    fieldFloor: fieldFloor,
    maxTileDepth: maxTileDepth,
    fieldCap: fieldCap,
    tvPlacement: tvPlacement
  };
})();
